import { useEffect, useRef } from 'react';

// A short, dependency-free confetti burst on a full-cover canvas — fires once
// on mount and fades out over ~2s. Brand colours only (teal, gold, success
// green, on-dark teal). Skipped entirely under prefers-reduced-motion: the
// confirmation still lands, just without the celebration.
const COLORS = ['#14564b', '#b8915a', '#2a6f50', '#7fd7c4'];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  rot: number;
  vr: number;
  color: string;
}

export default function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const parent = canvas.parentElement;
    const W = (canvas.width = parent?.clientWidth ?? window.innerWidth);
    const H = (canvas.height = parent?.clientHeight ?? 400);

    const parts: Particle[] = Array.from({ length: 90 }, () => ({
      x: W / 2 + (Math.random() - 0.5) * W * 0.35,
      y: H * 0.18,
      vx: (Math.random() - 0.5) * 8,
      vy: Math.random() * -9 - 2,
      w: 5 + Math.random() * 5,
      h: 7 + Math.random() * 6,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    }));

    const GRAVITY = 0.32;
    const LIFETIME = 150; // frames (~2.5s at 60fps)
    let raf = 0;
    let t = 0;

    function frame() {
      t += 1;
      ctx!.clearRect(0, 0, W, H);
      const alpha = Math.max(0, 1 - t / LIFETIME);
      for (const p of parts) {
        p.vy += GRAVITY;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx!.save();
        ctx!.globalAlpha = alpha;
        ctx!.translate(p.x, p.y);
        ctx!.rotate(p.rot);
        ctx!.fillStyle = p.color;
        ctx!.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx!.restore();
      }
      if (t < LIFETIME) raf = requestAnimationFrame(frame);
      else ctx!.clearRect(0, 0, W, H);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 w-full h-full"
    />
  );
}
